// apps/web/src/features/crm/leads/card/LeadCardScore.tsx
//
// Header qualification-score control (#2317 D-1). Read-only viewers (a pool /
// non-owner card, or a terminal read-only card) keep the static
// `EngagementScore` meter exactly as #2308 rendered it. An editor (admin, or
// the assigned manager) gets an EDITABLE `Rating` wired to `useUpdateLeadScore`
// — clicking a star sets 1..5, clicking the current value clears to null. When
// the lead is still UNqualified (`score === null`), an editor also sees a
// qualify prompt with a reduced-motion-safe breathing attention cue (the
// global `prefers-reduced-motion` rule collapses the animation).
import { useTranslation } from 'react-i18next';
import { useUpdateLeadScore } from '@magimp/shared-hooks';
import { Rating } from '../../../../ui/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { errorMessage } from '../../../../api/errors.js';
import { EngagementScore } from '../list/EngagementScore.js';

export interface LeadCardScoreProps {
  leadId: string;
  score: number | null;
  /** Editable only for an admin/assigned editor on a non-read-only card. */
  canEdit: boolean;
}

export function LeadCardScore({ leadId, score, canEdit }: LeadCardScoreProps) {
  const { t } = useTranslation(['leads']);
  const show = useToastStore((s) => s.show);
  const update = useUpdateLeadScore(leadId);

  if (!canEdit) return <EngagementScore score={score} size={17} />;

  const onChange = (next: number | null) => {
    update.mutate(
      { score: next },
      { onError: (err) => show({ tone: 'error', text: errorMessage(err) }) },
    );
  };

  const label =
    score === null ? t('leads:card.score.unrated') : t('leads:card.score.ariaLabel', { score });

  return (
    <span
      data-testid="lead-card-score-edit"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
    >
      {score === null && (
        <>
          {/* Reduced-motion-safe: the global prefers-reduced-motion rule collapses
              animation-duration to ~0 and iteration-count to 1 (tokens.css). */}
          <style>{'@keyframes magimp-qualify-breathe{0%,100%{opacity:1}50%{opacity:.55}}'}</style>
          <span
            data-testid="lead-card-qualify-prompt"
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--status-warn-fg)',
              whiteSpace: 'nowrap',
              animation: 'magimp-qualify-breathe var(--dur-attn-breathe) ease-in-out infinite',
            }}
          >
            {t('leads:card.score.qualifyPrompt')}
          </span>
        </>
      )}
      <Rating
        value={score}
        size={17}
        onChange={onChange}
        label={label}
        emptyLabel={t('leads:card.score.unrated')}
      />
    </span>
  );
}
